import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  BookOpen,
  UserCircle2,
  Mic,
  Zap,
  FileText,
  PhoneOff,
  MapPin,
  Wallet,
  GraduationCap,
  FileBadge,
  Signal,
  Volume2,
} from "lucide-react";

const FEATURES = [
  {
    key: "scenario",
    n: "01",
    icon: BookOpen,
    eyebrow: "Situation briefing",
    title: "Every role play starts with a real situation.",
    body:
      "Agents step into a scenario their team actually faces — not a generic drill. Think: a parent of a 10th-grade CBSE student exploring online tuitions.",
  },
  {
    key: "persona",
    n: "02",
    icon: UserCircle2,
    eyebrow: "AI persona",
    title: "The AI takes on a specific persona.",
    body:
      "Designation, background, paying capacity, mood — every detail you'd want to know before the real call. The persona behaves accordingly through the conversation.",
  },
  {
    key: "voice",
    n: "03",
    icon: Mic,
    eyebrow: "Voice-to-voice",
    title: "A real back-and-forth voice conversation.",
    body:
      "No typing. No scripts. Your team speaks, the AI listens, responds, and pushes back — just like a real customer call.",
  },
  {
    key: "interrupt",
    n: "04",
    icon: Zap,
    eyebrow: "Realistic interruptions",
    title: "The AI interrupts, just like real customers do.",
    body:
      "Mid-pitch pushback, skeptical questions, sudden topic shifts — the AI breaks in the way real buyers, parents and clients actually do.",
  },
  {
    key: "docs",
    n: "05",
    icon: FileText,
    eyebrow: "Show what matters",
    title: "Share documents inside the call.",
    body:
      "Pull up a brochure, a feature sheet, or a price list during the call — and the AI reacts to what you show, just like a live prospect.",
  },
];

export default function RolePlay() {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);
  // Mock lags text by ~320ms so text appears first, then image animation fires
  const [mockActive, setMockActive] = useState(0);
  const [isPinned, setIsPinned] = useState(false);

  // Each feature occupies 80vh of scroll distance. Plus 100vh for pin viewing.
  const STEP_VH = 80;
  const TOTAL_VH = 100 + FEATURES.length * STEP_VH;

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const scrollable = el.offsetHeight - vh;

      // Show content as soon as section starts entering viewport
      setIsPinned(rect.top <= vh * 0.1);

      const raw = -rect.top / scrollable;
      const progress = Math.min(1, Math.max(0, raw));
      const idx = Math.min(
        FEATURES.length - 1,
        Math.floor(progress * FEATURES.length + 0.0001)
      );
      setActive(idx);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setMockActive(active), 320);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section
      ref={sectionRef}
      id="role-plays"
      data-testid="roleplay-section"
      className="relative isolate"
      style={{
        minHeight: `${TOTAL_VH}vh`,
        background:
          "linear-gradient(180deg, #ffffff 0%, #f8f5ff 40%, #eff6ff 100%)",
      }}
    >
      {/* Ambient blobs (kept inside a clipped wrapper so they don't break sticky) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div
          className="absolute top-[8%] -left-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(139,92,246,0.28), transparent 70%)",
          }}
        />
        <div
          className="absolute top-[55%] -right-20 w-[560px] h-[560px] rounded-full blur-3xl opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(244,114,182,0.22), transparent 70%)",
          }}
        />
      </div>

      {/* Sticky pinned viewport: mobile — copy is intrinsic height; voice mock fills remaining viewport */}
      <div className="sticky top-0 h-[100dvh] max-h-[100dvh] flex flex-col items-stretch overflow-hidden lg:justify-center">
        <div className="relative w-full min-w-0 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex flex-col flex-1 min-h-0 lg:block lg:flex-none">
          {/* ── Centered section header (always visible during pin) ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isPinned ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-left max-w-3xl mr-auto mb-5 sm:mb-2 lg:mb-14 shrink-0 pt-5 sm:pt-7 lg:pt-10"
          >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.2em] sm:tracking-[0.22em] text-violet-700 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-violet-50 border border-violet-100">
              <Sparkles size={11} className="sm:w-3 sm:h-3" strokeWidth={2.5} />
              Practice · AI role plays
            </div>
            <h2 className="mt-2 sm:mt-5 text-[22px] sm:text-[34px] lg:text-[46px] leading-[1.08] sm:leading-[1.05] font-extrabold tracking-tight text-slate-900 text-balance">
              Not just learning.{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(115deg, #7c3aed 0%, #ec4899 60%, #f59e0b 100%)",
                }}
              >
                Real voice practice.
              </span>
            </h2>
          </motion.div>

          {/* ── Grid: single active feature (left) + mock (right); mobile stacks with mock at bottom ── */}
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-0 sm:gap-0 lg:gap-16 items-stretch lg:items-center flex-1 min-h-0 min-w-0 max-lg:grid-rows-[auto_minmax(0,1fr)] lg:grid-rows-none">
            {/* LEFT — single feature swapped via AnimatePresence */}
            <div className="relative shrink-0 max-lg:overflow-visible lg:min-h-[320px] flex items-start lg:items-center lg:overflow-visible pr-0.5 lg:[scrollbar-gutter:stable]">
              <AnimatePresence mode="wait">
                {isPinned && (
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 32, filter: "blur(4px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -32, filter: "blur(4px)" }}
                    transition={{
                      duration: 0.55,
                      ease: [0.22, 1, 0.36, 1],
                      delay: active === 0 ? 0.6 : 0,
                    }}
                    className="max-w-xl"
                    data-testid={`roleplay-feature-${FEATURES[active].key}`}
                  >
                    <div className="text-[10px] sm:text-[12px] font-bold uppercase tracking-[0.2em] sm:tracking-[0.24em] text-violet-700">
                      {FEATURES[active].n} · {FEATURES[active].eyebrow}
                    </div>
                    <h3 className="mt-2 sm:mt-4 text-[19px] sm:text-[26px] lg:text-[38px] leading-[1.12] sm:leading-[1.1] font-extrabold tracking-tight text-slate-900 text-balance">
                      {FEATURES[active].title}
                    </h3>
                    <p className="mt-3 sm:mt-5 text-[13.5px] sm:text-[15px] lg:text-[16.5px] leading-relaxed text-slate-600 max-w-md">
                      {FEATURES[active].body}
                    </p>
                    <div className="mt-4 sm:mt-7 flex items-center gap-2 sm:gap-3">
                      <span
                        className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl text-white shadow-[0_12px_26px_-10px_rgba(124,58,237,0.7)] shrink-0"
                        style={{
                          background:
                            "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
                        }}
                      >
                        {React.createElement(FEATURES[active].icon, {
                          size: 18,
                          strokeWidth: 2.3,
                        })}
                      </span>
                      {/* Progress pill indicator */}
                      <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap min-w-0">
                        {FEATURES.map((_, i) => (
                          <span
                            key={i}
                            className={`h-1 sm:h-1.5 rounded-full transition-all duration-500 shrink-0 ${
                              i === active
                                ? "w-6 sm:w-8 bg-gradient-to-r from-violet-500 to-pink-400"
                                : i < active
                                  ? "w-3 sm:w-4 bg-slate-300"
                                  : "w-3 sm:w-4 bg-slate-200"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* RIGHT — voice-call mock (mobile: fills all vertical space left after header + copy) */}
            <motion.div
              className="min-w-0 w-full max-w-full max-lg:min-h-0 max-lg:h-full max-lg:flex max-lg:flex-col max-lg:justify-end lg:shrink-0 pt-0 max-lg:pb-[max(0.35rem,env(safe-area-inset-bottom,0px))] lg:pb-0 max-lg:mx-auto flex justify-center items-end"
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={
                isPinned
                  ? { opacity: 1, y: 0, scale: 1 }
                  : { opacity: 0, y: 30, scale: 0.96 }
              }
              transition={{
                duration: 0.75,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.25,
              }}
            >
              <VoiceCallMock active={mockActive} />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =====================================================================
// VoiceCallMock – big phone-call-style portrait with overlays per step
// =====================================================================
const PERSONA_PHOTO =
  "https://asset.sparkl.me/pb/poc-folder/QpimOVqSu38Uxn4FYSkIiiHWyvWv9LA4c1VYILO5.png";

function VoiceCallMock({ active }) {
  return (
    <div
      className="relative w-full min-w-0 max-w-[min(460px,100%)] mx-auto max-lg:px-0 max-lg:h-full max-lg:flex max-lg:justify-center max-lg:items-end"
      data-testid="roleplay-voicecall-mock"
    >
      {/* Phone frame — mobile: width-first + max-height so aspect ratio never exceeds viewport */}
      <div
        className="relative mx-auto w-full min-w-0 max-w-full aspect-[3/4] h-auto max-h-full shrink-0 rounded-[28px] lg:rounded-[40px] overflow-hidden bg-slate-900 shadow-[0_28px_70px_-24px_rgba(15,23,42,0.5)] lg:shadow-[0_50px_120px_-30px_rgba(15,23,42,0.55)] border border-slate-200"
      >
        {/* Big persona photo — fills card (cover) */}
        <img
          src={PERSONA_PHOTO}
          alt="Ms. Priya Mehta"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark gradient for readability (top + bottom) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,14,32,0.55) 0%, rgba(10,14,32,0) 22%, rgba(10,14,32,0) 60%, rgba(10,14,32,0.88) 100%)",
          }}
        />

        {/* ─── TOP STATUS BAR ─── */}
        <div className="absolute top-0 inset-x-0 px-3 pt-3 lg:px-5 lg:pt-5 flex items-center justify-between gap-2 z-20 text-white">
          <span className="inline-flex items-center gap-1 sm:gap-1.5 text-[8.5px] lg:text-[10.5px] font-bold uppercase tracking-[0.18em] lg:tracking-[0.22em] bg-emerald-500/90 backdrop-blur px-2 lg:px-2.5 py-0.5 lg:py-1 rounded-full shrink-0 max-w-[55%]">
            <span className="w-1 h-1 lg:w-1.5 lg:h-1.5 rounded-full bg-white animate-pulse shrink-0" />
            <span className="truncate">Live · 00:47</span>
          </span>
          <span className="inline-flex items-center gap-0.5 lg:gap-1 text-[9px] lg:text-[11px] font-semibold bg-white/10 backdrop-blur border border-white/20 px-2 lg:px-2.5 py-0.5 lg:py-1 rounded-full shrink-0">
            <Signal size={10} className="lg:w-[11px] lg:h-[11px]" strokeWidth={2.5} />
            <span className="hidden sm:inline">Voice call</span>
            <span className="sm:hidden">Call</span>
          </span>
        </div>

        {/* ─── STEP OVERLAYS ─── */}
        {/* Step 0 — Situation badge top-center */}
        <AnimatePresence>
          {active === 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="absolute top-12 lg:top-16 left-3 right-3 lg:left-5 lg:right-5 z-30"
              data-testid="rp-overlay-scenario"
            >
              <div className="rounded-xl lg:rounded-2xl bg-white/30 backdrop-blur-2xl border border-white/40 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.5)] p-2.5 lg:p-4">
                <div className="flex items-center gap-1.5 lg:gap-2 text-[8.5px] lg:text-[10.5px] font-bold uppercase tracking-[0.18em] lg:tracking-[0.22em] text-violet-700 mb-1.5 lg:mb-2">
                  <BookOpen size={12} className="lg:w-[13px] lg:h-[13px]" strokeWidth={2.4} />
                  Situation
                </div>
                <div className="text-[12px] lg:text-[14.5px] font-bold text-slate-900 leading-snug">
                  Parent of 10th grade CBSE student
                </div>
                <div className="text-[10.5px] lg:text-[12.5px] text-white mt-0.5 lg:mt-1 leading-snug">
                  Exploring online tuitions · wants faster board-exam prep
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 1 — Persona details card (right-side floater) */}
        <AnimatePresence>
          {active === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 28 }}
              transition={{ duration: 0.45 }}
              className="absolute top-14 lg:top-20 left-3 right-3 lg:left-auto lg:right-4 z-30 w-auto lg:w-[220px] max-w-none lg:max-w-[220px]"
              data-testid="rp-overlay-persona"
            >
              <div className="rounded-xl lg:rounded-2xl bg-white/30 backdrop-blur-2xl border border-white/40 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.5)] p-2.5 lg:p-4">
                <div className="flex items-center gap-1.5 lg:gap-2 text-[8.5px] lg:text-[10.5px] font-bold uppercase tracking-[0.18em] lg:tracking-[0.22em] text-blue-700 mb-1.5 lg:mb-2.5">
                  <UserCircle2 size={12} className="lg:w-[13px] lg:h-[13px]" strokeWidth={2.4} />
                  AI persona
                </div>
                <div className="text-[12px] lg:text-[14.5px] font-bold text-slate-900 leading-tight">
                  Ms. Priya Mehta
                </div>
                <div className="text-[10px] lg:text-[11.5px] text-white mt-0.5 leading-snug">
                  Parent · cautious, comparing options
                </div>
                <div className="mt-3 space-y-1.5 ">
                  <PersonaRow icon={MapPin} label="Mumbai" />
                  <PersonaRow icon={Wallet} label="Budget ₹15k / month" />
                  <PersonaRow icon={GraduationCap} label="Class 10 · CBSE" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 2 — Voice waveform across mid-photo */}
        <AnimatePresence>
          {active === 2 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-x-2 lg:inset-x-4 top-[34%] lg:top-[38%] z-30 flex flex-col items-center"
              data-testid="rp-overlay-voice"
            >
              <div className="rounded-xl lg:rounded-2xl bg-white/10 backdrop-blur-2xl border border-white/25 px-3 py-2.5 lg:px-5 lg:py-4 w-full shadow-[0_16px_40px_-16px_rgba(0,0,0,0.5)]">
                <div className="flex items-center justify-center gap-1 lg:gap-1.5 text-[8px] lg:text-[10px] font-bold uppercase tracking-[0.22em] lg:tracking-[0.28em] text-cyan-300 mb-1.5 lg:mb-2">
                  <Mic size={10} className="lg:w-[11px] lg:h-[11px]" strokeWidth={2.6} />
                  Listening…
                </div>
                <BigWaveform />
                <div className="mt-1.5 lg:mt-2 text-[9px] lg:text-[11px] text-slate-300 text-center font-medium">
                  Real-time voice conversation
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 3 — Interrupt banner + lightning flash */}
        <AnimatePresence>
          {active === 3 && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 z-20 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 40% at 50% 40%, rgba(244,63,94,0.25), transparent 70%)",
                }}
              />
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-x-2 lg:inset-x-4 top-[32%] lg:top-[36%] z-30"
                data-testid="rp-overlay-interrupt"
              >
                <div
                  className="rounded-xl lg:rounded-2xl p-2.5 lg:p-4 flex items-center gap-2 lg:gap-3 shadow-[0_18px_40px_-12px_rgba(244,63,94,0.55)] border backdrop-blur-2xl"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,247,237,0.35) 0%, rgba(255,228,230,0.35) 100%)",
                    borderColor: "rgba(244,63,94,0.4)",
                  }}
                >
                  <span
                    className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl flex items-center justify-center text-white flex-shrink-0 animate-pulse"
                    style={{
                      background:
                        "linear-gradient(135deg, #f43f5e 0%, #f59e0b 100%)",
                    }}
                  >
                    <Zap size={16} className="lg:w-[18px] lg:h-[18px]" strokeWidth={2.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[8px] lg:text-[10px] font-bold uppercase tracking-[0.18em] lg:tracking-[0.22em] text-rose-700">
                      AI interrupted
                    </div>
                    <div className="text-[11px] lg:text-[13px] font-semibold text-slate-200 leading-snug mt-0.5 ">
                      "Wait — before pricing, tell me what's different from the
                      tuition my neighbour uses."
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Step 4 — Document card floating near bottom-right, overflowing frame */}
        <AnimatePresence>
          {active === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 20, x: 20, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.95 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-24 z-30 left-auto right-3 w-[min(230px,calc(100%-0.75rem))] lg:-right-6 lg:w-[230px]"
              data-testid="rp-overlay-document"
            >
              <div className="rounded-2xl bg-white/25 backdrop-blur-2xl border border-white/40 shadow-[0_24px_60px_-20px_rgba(37,99,235,0.5)] overflow-hidden">
                <div
                  className="h-1"
                  style={{
                    background:
                      "linear-gradient(90deg, #3b82f6 0%, #7c3aed 60%, #ec4899 100%)",
                  }}
                />
                <div className="p-2.5 lg:p-3.5">
                  <div className="flex items-center gap-2 lg:gap-2.5">
                    <span
                      className="w-8 h-8 lg:w-9 lg:h-9 rounded-md lg:rounded-lg flex items-center justify-center text-white flex-shrink-0"
                      style={{
                        background:
                          "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                      }}
                    >
                      <FileBadge size={15} className="lg:w-[17px] lg:h-[17px]" strokeWidth={2.2} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[8px] lg:text-[9.5px] font-bold uppercase tracking-wider text-blue-700">
                        Sharing with AI
                      </div>
                      <div className="text-[11px] lg:text-[12.5px] font-bold text-slate-200 leading-tight truncate">
                        CBSE_Class10_Brochure.pdf
                      </div>
                    </div>
                  </div>
                  <div className="mt-2.5 rounded-lg border border-white/30 bg-white/20 backdrop-blur-sm p-2.5">
                    <div className="flex items-center gap-1 mb-1.5">
                      <span className="w-1 h-1 rounded-full bg-rose-300" />
                      <span className="w-1 h-1 rounded-full bg-amber-300" />
                      <span className="w-1 h-1 rounded-full bg-emerald-300" />
                      <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-400">
                        Page 1 · live
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="h-1.5 rounded-full bg-slate-200 w-3/4" />
                      <div className="h-1.5 rounded-full bg-slate-200 w-5/6" />
                      <div className="h-1.5 rounded-full bg-slate-200 w-2/3" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── BOTTOM NAME + CALL CONTROLS ─── */}
        <div className="absolute bottom-0 inset-x-0 p-3.5 pb-4 lg:p-6 z-20 text-white">
          <div className="text-[8.5px] lg:text-[11px] font-bold uppercase tracking-[0.16em] lg:tracking-[0.22em] text-cyan-200 leading-tight">
            AI Role play · Parent persona
          </div>
          <div className="mt-0.5 lg:mt-1 text-[17px] lg:text-[24px] font-extrabold tracking-tight leading-tight">
            Ms. Priya Mehta
          </div>
          <div className="text-[10.5px] lg:text-[13px] text-slate-200 opacity-90 leading-snug">
            Mumbai · Budget ₹15k / mo
          </div>

          <div className="mt-3 lg:mt-5 flex items-center gap-2 lg:gap-3">
            <CallControlBtn icon={Mic} label="Mute" />
            <CallControlBtn icon={Volume2} label="Speaker" />
            <CallControlBtn icon={FileBadge} label="Share" active={active === 4} />
            <button
              className="ml-auto w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center text-white shadow-[0_14px_30px_-8px_rgba(239,68,68,0.75)] shrink-0"
              style={{
                background:
                  "linear-gradient(135deg, #ef4444 0%, #f43f5e 100%)",
              }}
              aria-label="End call"
            >
              <PhoneOff size={16} className="lg:w-[18px] lg:h-[18px]" strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </div>

      {/* Agent pill (representing YOU) — floats at bottom-left overlapping frame */}
      <div
        className="absolute left-0 lg:-left-5 bottom-12 lg:bottom-16 z-40 max-w-[calc(100%-0.25rem)] rounded-full bg-white/30 backdrop-blur-2xl border border-white/50 shadow-[0_14px_30px_-10px_rgba(15,23,42,0.3)] pl-1 lg:pl-1.5 pr-2.5 lg:pr-4 py-1 lg:py-1.5 flex items-center gap-1.5 lg:gap-2"
        data-testid="rp-agent-pill"
      >
        <span
          className="w-7 h-7 lg:w-9 lg:h-9 rounded-full flex items-center justify-center text-white font-bold text-[10px] lg:text-[12px] shrink-0"
          style={{
            background:
              "linear-gradient(135deg, #0f172a 0%, #475569 100%)",
          }}
        >
          YA
        </span>
        <div className="flex flex-col min-w-0">
          <span className="text-[8px] lg:text-[9.5px] font-bold uppercase tracking-wider text-slate-100 leading-none truncate">
            You · Agent
          </span>
          <span className={`mt-0.5 lg:mt-1 text-[9px] lg:text-[10.5px] font-semibold leading-none truncate ${
            active === 2 ? "text-emerald-600" : "text-slate-300"
          }`}>
            {active === 2 ? "● Speaking" : "Listening"}
          </span>
        </div>
      </div>
    </div>
  );
}

function PersonaRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-[10.5px] lg:text-[12px] text-slate-200">
      <Icon size={11} strokeWidth={2.4} className="text-white flex-shrink-0 lg:w-3 lg:h-3" />
      <span className="truncate">{label}</span>
    </div>
  );
}

function CallControlBtn({ icon: Icon, label, active = false }) {
  return (
    <button
      aria-label={label}
      className={`w-10 h-10 lg:w-12 lg:h-12 shrink-0 rounded-full flex items-center justify-center border transition-all ${
        active
          ? "bg-cyan-400/30 border-cyan-300 text-white shadow-[0_0_24px_-4px_rgba(34,211,238,0.7)]"
          : "bg-white/10 border-white/20 text-white hover:bg-white/15 backdrop-blur"
      }`}
    >
      <Icon className="w-[15px] h-[15px] lg:w-[17px] lg:h-[17px]" strokeWidth={2.2} />
    </button>
  );
}

function BigWaveform() {
  const BARS = 36;
  return (
    <div className="w-full max-w-full flex items-center justify-center gap-[2px] lg:gap-[3px] h-8 lg:h-10 overflow-hidden px-0.5">
      {Array.from({ length: BARS }).map((_, i) => {
        const d = Math.abs(i - (BARS - 1) / 2) / ((BARS - 1) / 2);
        const baseHeight = Math.round((1 - d) * 32 + 6);
        return (
          <span
            key={i}
            className="block rounded-full voice-wave-bar"
            style={{
              width: 3,
              height: baseHeight,
              background:
                "linear-gradient(to top, #22d3ee, #a78bfa, #f472b6)",
              animationDelay: `${(i * 0.06) % 1.1}s`,
              filter: "drop-shadow(0 0 4px rgba(167,139,250,0.7))",
            }}
          />
        );
      })}
    </div>
  );
}
